import React, { useEffect, useState } from 'react';
import { ConfigurationSection, SpaceBetweenRow } from "@features/settings/pages/StyleComponents";
import { BodyLarge, BodyRegular } from "../../atoms/fonts/Body";
import Colours from "../../atoms/Colours";
import { Switch } from "../../atoms/Switch";
import { Checkbox } from "../../atoms/Checkbox";
import { useDispatch } from "react-redux";
import { failureNotification } from '@shared/store/notification.duck';
import { SimpleSpinner } from "../../atoms/SimpleSpinner";
import { useTranslation } from "react-i18next";
import { ReportRecipientType, ReportType } from '@shared/models/report.models';
import { getReportConfigs, updateReportConfig } from '@features/settings/services/ReportService';
import { SendSampleReportButton } from "./SendSampleReportButton";

export function MonthlyOfficeSummarySetting() {
  const [isLoading, setIsLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [recipientTypes, setRecipientTypes] = useState<ReportRecipientType[]>([ReportRecipientType.COMPANY_ADMINS]);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    let active = true;
    getReportConfigs()
      .then(configs => {
        if (!active) {
          return;
        }
        const config = configs.find(c => c.reportType === ReportType.MONTHLY_OFFICE_SUMMARY);
        if (config) {
          setEnabled(config.enabled);
          if (config.recipientTypes && config.recipientTypes.length > 0) {
            setRecipientTypes(config.recipientTypes);
          }
        }
      })
      .catch(() => { /* leave defaults in place */ });
    return () => { active = false; };
  }, []);

  const persist = async (next: { enabled: boolean; recipientTypes: ReportRecipientType[] }) => {
    try {
      setIsLoading(true);
      await updateReportConfig({
        reportType: ReportType.MONTHLY_OFFICE_SUMMARY,
        enabled: next.enabled,
        recipientTypes: next.recipientTypes,
      });
      setEnabled(next.enabled);
      setRecipientTypes(next.recipientTypes);
    } catch (e: any) {
      dispatch(failureNotification('Failed to update setting, please wait a minute.'));
    } finally {
      setIsLoading(false);
    }
  };

  const onToggle = (value: boolean) => persist({ enabled: value, recipientTypes });

  const toggleRecipient = (type: ReportRecipientType, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...recipientTypes, type]))
      : recipientTypes.filter(r => r !== type);
    persist({ enabled, recipientTypes: next });
  };

  return (
    <ConfigurationSection>
      <SpaceBetweenRow>
        <BodyLarge colour={Colours.blue}>{t('settings.office-summary.title')}</BodyLarge>
        {isLoading ? <SimpleSpinner size={32} /> : <Switch value={enabled} label="" onChange={onToggle} />}
      </SpaceBetweenRow>
      <BodyRegular style={{ marginBottom: 16 }}>{t('settings.office-summary.subtitle')}</BodyRegular>
      <SendSampleReportButton reportType={ReportType.MONTHLY_OFFICE_SUMMARY} />

      {enabled && (
        <>
          <BodyRegular weight={600} style={{ marginBottom: 8 }}>{t('settings.office-summary.recipients.label')}</BodyRegular>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Checkbox text={'settings.office-summary.recipients.company-admins'}
                      value={recipientTypes.includes(ReportRecipientType.COMPANY_ADMINS)}
                      disabled={isLoading}
                      onChange={(checked: boolean) => toggleRecipient(ReportRecipientType.COMPANY_ADMINS, checked)} />
            <Checkbox text={'settings.office-summary.recipients.office-admins'}
                      value={recipientTypes.includes(ReportRecipientType.OFFICE_ADMINS)}
                      disabled={isLoading}
                      onChange={(checked: boolean) => toggleRecipient(ReportRecipientType.OFFICE_ADMINS, checked)} />
          </div>
        </>
      )}
    </ConfigurationSection>
  );
}
